/**
 * PerformancePage.js — Performance Management
 *
 * Validations added (4 modals):
 * 1. GoalFormModal:    title req/min2/max200, target_value >0 integer, due_date not past
 * 2. GiveFeedbackModal: receiver required, feedback req/min10/max1000
 * 3. CycleFormModal:  name req/min2/max150, start required, end required, end>start,
 *                     all deadlines must be within [start, end]
 * 4. PIPFormModal:    employee required, title req/min5/max200,
 *                     reason/improvement_areas/success_criteria req/min20/max2000,
 *                     start required, end required, end>start, end≥2 weeks from start
 *
 * window.confirm in CyclesTab (launch cycle) kept — destructive irreversible action
 * is appropriate for a native confirm dialog.
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, Alert, Table, ProgressBar,
} from 'react-bootstrap';
import {
    Target, Star, MessageSquare, TrendingUp, Users,
    Plus, CheckCircle, Clock, AlertTriangle,
    Edit2, BarChart2, RefreshCw, ThumbsUp, Zap,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES       = ['super-admin','admin','hr-manager','account-manager'];
const CYCLE_TYPES    = ['annual','mid-year','quarterly','probation'];
const GOAL_CATEGORIES= ['Individual','Team','Department','Company'];
const FEEDBACK_TYPES = [
    { key:'recognition',  label:'🌟 Recognition', desc:'Praise great work'   },
    { key:'constructive', label:'💡 Constructive', desc:'Suggest improvement' },
    { key:'general',      label:'💬 General',      desc:'General feedback'    },
];
const REVIEW_STATUS = {
    pending:     { bg:'warning', label:'Pending'     },
    in_progress: { bg:'info',    label:'In Progress' },
    completed:   { bg:'success', label:'Completed'   },
};

// ── Helpers ───────────────────────────────────────────────────────────────
const StarInput = ({ value, onChange, readOnly }) => (
    <div className="d-flex gap-1">
        {[1,2,3,4,5].map(n => (
            <Star key={n} size={22}
                onClick={() => !readOnly && onChange?.(n)}
                style={{
                    cursor: readOnly ? 'default' : 'pointer',
                    color: n <= (value||0) ? '#f59e0b' : '#d1d5db',
                    fill:  n <= (value||0) ? '#f59e0b' : 'transparent',
                }}/>
        ))}
        {value > 0 && <span className="ms-1 text-muted small">{value}/5</span>}
    </div>
);

const formatDate = (d) => {
    if (!d) return '—';
    const [y,m,dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y,m-1,dd).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
};

const ProgressRing = ({ pct, size=56, stroke=5, color='#4f46e5' }) => {
    const r = (size-stroke)/2, circ = 2*Math.PI*r;
    return (
        <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke}/>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
                strokeWidth={stroke} strokeDasharray={circ}
                strokeDashoffset={circ-(pct/100)*circ} strokeLinecap="round"/>
        </svg>
    );
};

const todayStr = () => new Date().toISOString().slice(0,10);

// ── Inline field error helper ─────────────────────────────────────────────
const FE = ({ msg }) => msg
    ? <div className="text-danger mt-1" style={{fontSize:12.5}}>{msg}</div>
    : null;

// ════════════════════════════════════════════════════════════════════════════
const PerformancePage = () => {
    const { user } = useAuth();
    const isHR  = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState('goals');
    const tabs = [
        { key:'goals',      label:'My Goals',      icon:Target,        show:true   },
        { key:'reviews',    label:'Reviews',        icon:Star,          show:true   },
        { key:'feedback',   label:'Feedback',       icon:MessageSquare, show:true   },
        { key:'appraisals', label:'Appraisals',     icon:TrendingUp,    show:true   },
        { key:'cycles',     label:'Review Cycles',  icon:RefreshCw,     show:isHR   },
        { key:'pip',        label:'PIP',            icon:AlertTriangle, show:isHR   },
    ].filter(t => t.show);
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <TrendingUp size={22} className="me-2 text-primary"/>Performance Management
                    </h4>
                    <p className="text-muted small mb-0">Goals · Reviews · Feedback · Appraisals</p>
                </div>
            </div>
            <div className="d-flex gap-1 flex-wrap mb-4">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab===t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
            </div>
            {tab==='goals'      && <GoalsTab isHR={isHR} user={user}/>}
            {tab==='reviews'    && <ReviewsTab isHR={isHR} user={user}/>}
            {tab==='feedback'   && <FeedbackTab isHR={isHR}/>}
            {tab==='appraisals' && <AppraisalsTab isHR={isHR}/>}
            {tab==='cycles'     && isHR && <CyclesTab/>}
            {tab==='pip'        && isHR && <PIPTab/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// GOALS TAB
// ════════════════════════════════════════════════════════════════════════════
const GoalsTab = ({ isHR, user }) => {
    const [goals,   setGoals]         = useState([]);
    const [loading, setLoading]       = useState(true);
    const [showNew, setShowNew]       = useState(false);
    const [progressModal, setProgressModal] = useState(null);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/goals')
            .then(r => setGoals(r.data.data || []))
            .catch(() => toast.error('Failed to load goals'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    const updateProgress = async (goalId, currentValue, notes) => {
        try {
            await axiosInstance.patch(`/performance/goals/${goalId}/progress`,
                { current_value: currentValue, progress_notes: notes });
            toast.success('Progress updated!');
            setProgressModal(null);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    const active    = goals.filter(g => g.status === 'active');
    const completed = goals.filter(g => g.status === 'completed');
    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <div className="d-flex gap-2">
                    <Badge bg="primary">{active.length} Active</Badge>
                    <Badge bg="success">{completed.length} Completed</Badge>
                </div>
                <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
                    <Plus size={14} className="me-1"/>New Goal
                </Button>
            </div>
            {goals.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <Target size={40} className="mx-auto mb-3 text-muted opacity-50"/>
                    <p className="text-muted">No goals yet. Set your first goal to get started.</p>
                    <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
                        <Plus size={14} className="me-1"/>Set a goal
                    </Button>
                </Card>
            ) : (
                <Row className="g-3">
                    {goals.map(g => (
                        <Col key={g.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100 p-4"
                                style={{borderLeft:`4px solid ${g.status==='completed'?'#059669':'#4f46e5'}`}}>
                                <div className="d-flex justify-content-between mb-2">
                                    <Badge bg="light" text="dark" style={{fontSize:10}}>{g.cycle}</Badge>
                                    <Badge bg="light" text="dark" style={{fontSize:10}}>{g.category}</Badge>
                                </div>
                                {g.employee_name && g.employee_id !== user?.id && (
                                    <div className="text-muted small mb-1">{g.employee_name}</div>
                                )}
                                <h6 className="fw-bold mb-3">{g.title}</h6>
                                {g.description && (
                                    <p className="text-muted small mb-3" style={{
                                        overflow:'hidden',display:'-webkit-box',
                                        WebkitLineClamp:2,WebkitBoxOrient:'vertical',
                                    }}>{g.description}</p>
                                )}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between small mb-1">
                                        <span className="text-muted">Progress</span>
                                        <span className="fw-bold">{g.current_value}/{g.target_value} ({g.progress_pct||0}%)</span>
                                    </div>
                                    <ProgressBar now={parseFloat(g.progress_pct||0)}
                                        variant={g.status==='completed'?'success':'primary'} style={{height:8}}/>
                                </div>
                                {g.due_date && (
                                    <div className="text-muted small mb-3">
                                        <Clock size={11} className="me-1"/>Due {formatDate(g.due_date)}
                                    </div>
                                )}
                                <div className="d-flex gap-2 mt-auto">
                                    {g.status === 'active' && (
                                        <Button size="sm" variant="outline-primary" className="flex-grow-1"
                                            onClick={() => setProgressModal(g)}>
                                            Update Progress
                                        </Button>
                                    )}
                                    {g.status === 'completed' && (
                                        <Badge bg="success" className="py-2 flex-grow-1 text-center">
                                            <CheckCircle size={12} className="me-1"/>Done!
                                        </Badge>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {showNew && (
                <GoalFormModal
                    onClose={() => setShowNew(false)}
                    onDone={() => { setShowNew(false); load(); }}
                    isHR={isHR}/>
            )}
            {progressModal && (
                <ProgressModal goal={progressModal}
                    onClose={() => setProgressModal(null)}
                    onSave={updateProgress}/>
            )}
        </>
    );
};

// ── Goal Form Modal — VALIDATIONS ─────────────────────────────────────────
const validateGoal = (form, isHR) => {
    const e = {};
    const title = form.title?.trim();
    if (!title)              e.title = 'Goal title is required';
    else if (title.length<2) e.title = 'Title must be at least 2 characters';
    else if (title.length>200) e.title = 'Title cannot exceed 200 characters';

    const tv = parseFloat(form.target_value);
    if (!form.target_value && form.target_value !== 0)
        e.target_value = 'Target value is required';
    else if (isNaN(tv) || tv <= 0)
        e.target_value = 'Target must be a positive number';
    else if (!Number.isInteger(tv) && tv > 999999)
        e.target_value = 'Target value too large';

    if (form.due_date && form.due_date < todayStr())
        e.due_date = 'Due date cannot be in the past';

    return e;
};

const GoalFormModal = ({ onClose, onDone, isHR }) => {
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        title:'', description:'', cycle:'Q1',
        category:'Individual', target_value:100,
        due_date:'', employee_id:'',
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    useEffect(() => {
        if (isHR) axiosInstance.get('/employees')
            .then(r => setEmployees(r.data.data?.employees || r.data.data || []))
            .catch(()=>{});
    }, [isHR]);

    const setField = (k, v) => {
        setForm(p => ({...p,[k]:v}));
        if (attempted) setErrors(prev => { const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validateGoal(form, isHR);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/performance/goals', form);
            toast.success('Goal created!');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Target size={18} className="me-2 text-primary"/>New Goal
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    {isHR && (
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Assign to Employee</Form.Label>
                            <Form.Select value={form.employee_id}
                                onChange={e => setField('employee_id', e.target.value)}>
                                <option value="">— Myself —</option>
                                {employees.map(e => (
                                    <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    )}
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Goal Title *</Form.Label>
                        <Form.Control
                            placeholder="e.g. Complete 3 client demos per week"
                            value={form.title}
                            maxLength={200}
                            isInvalid={attempted && !!errors.title}
                            onChange={e => setField('title', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control as="textarea" rows={2}
                            placeholder="Details, context, approach…"
                            value={form.description}
                            maxLength={1000}
                            onChange={e => setField('description', e.target.value)}/>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Cycle</Form.Label>
                        <Form.Select value={form.cycle}
                            onChange={e => setField('cycle', e.target.value)}>
                            {['Q1','Q2','Q3','Q4','H1','H2','Annual'].map(c => <option key={c}>{c}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Category</Form.Label>
                        <Form.Select value={form.category}
                            onChange={e => setField('category', e.target.value)}>
                            {GOAL_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Target Value *</Form.Label>
                        <Form.Control type="number" min="1" placeholder="100"
                            value={form.target_value}
                            isInvalid={attempted && !!errors.target_value}
                            onChange={e => setField('target_value', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.target_value}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Due Date</Form.Label>
                        <Form.Control type="date" value={form.due_date}
                            min={todayStr()}
                            isInvalid={attempted && !!errors.due_date}
                            onChange={e => setField('due_date', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.due_date}</Form.Control.Feedback>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Create Goal'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

const ProgressModal = ({ goal, onClose, onSave }) => {
    const [val,   setVal]   = useState(goal.current_value || 0);
    const [notes, setNotes] = useState('');
    const [valErr, setValErr] = useState('');
    const handleSave = () => {
        const n = parseFloat(val);
        if (isNaN(n) || n < 0) { setValErr('Enter a valid value (≥ 0)'); return; }
        if (n > parseFloat(goal.target_value)) { setValErr(`Cannot exceed target (${goal.target_value})`); return; }
        onSave(goal.id, val, notes);
    };
    return (
        <Modal show onHide={onClose} centered size="sm">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Update Progress</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">{goal.title}</p>
                <Form.Label className="small fw-bold">
                    Current value (target: {goal.target_value})
                </Form.Label>
                <Form.Control type="number" value={val}
                    min={0} max={goal.target_value}
                    isInvalid={!!valErr}
                    onChange={e => { setVal(e.target.value); setValErr(''); }}/>
                <Form.Control.Feedback type="invalid">{valErr}</Form.Control.Feedback>
                <div className="mt-2">
                    <ProgressBar
                        now={Math.min(100, Math.round((parseFloat(val)||0)/parseFloat(goal.target_value)*100))}
                        style={{height:8}}/>
                </div>
                <Form.Control className="mt-3" as="textarea" rows={2}
                    placeholder="What did you accomplish?"
                    value={notes} onChange={e => setNotes(e.target.value)}/>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1" onClick={handleSave}>Save Progress</Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// REVIEWS TAB — unchanged (no forms to add validations to)
// ════════════════════════════════════════════════════════════════════════════
const ReviewsTab = ({ isHR, user }) => {
    const [myReviews,   setMyReviews]   = useState([]);
    const [teamReviews, setTeamReviews] = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [selected,    setSelected]    = useState(null);
    const load = () => {
        setLoading(true);
        const calls = [axiosInstance.get('/performance/reviews/my')];
        if (isHR) calls.push(axiosInstance.get('/performance/reviews/team'));
        Promise.all(calls)
            .then(([my, team]) => {
                setMyReviews(my.data.data || []);
                if (team) setTeamReviews(team.data.data || []);
            })
            .catch(() => toast.error('Failed to load reviews'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    if (selected) return (
        <ReviewDetail review={selected} isHR={isHR} user={user}
            onBack={() => setSelected(null)} onRefresh={load}/>
    );
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    const ReviewRow = ({ r }) => (
        <tr style={{cursor:'pointer'}} onClick={() => setSelected(r)}>
            <td className="ps-4">
                <div className="fw-medium">{r.reviewee_name || 'Me'}</div>
                <div className="text-muted" style={{fontSize:10}}>{r.department}</div>
            </td>
            <td><Badge bg="light" text="dark">{r.cycle}</Badge></td>
            <td><Badge bg="light" text="dark">{r.review_type}</Badge></td>
            <td><Badge bg={REVIEW_STATUS[r.status]?.bg||'secondary'}>{REVIEW_STATUS[r.status]?.label||r.status}</Badge></td>
            <td>{r.overall_rating
                ? <StarInput value={Math.round(r.overall_rating)} readOnly/>
                : <span className="text-muted small">Not rated</span>}</td>
            <td className="pe-4">{formatDate(r.due_date)}</td>
        </tr>
    );
    return (
        <>
            {myReviews.length > 0 && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">Reviews I need to complete</Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th className="ps-4">Employee</th><th>Cycle</th>
                                <th>Type</th><th>Status</th><th>Rating</th><th className="pe-4">Due</th>
                            </tr>
                        </thead>
                        <tbody>{myReviews.map(r => <ReviewRow key={r.id} r={r}/>)}</tbody>
                    </Table>
                </Card>
            )}
            {isHR && teamReviews.length > 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">Team Reviews Overview</Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th className="ps-4">Employee</th><th>Cycle</th>
                                <th>Type</th><th>Status</th><th>Rating</th><th className="pe-4">Due</th>
                            </tr>
                        </thead>
                        <tbody>{teamReviews.map(r => <ReviewRow key={r.id} r={r}/>)}</tbody>
                    </Table>
                </Card>
            )}
            {myReviews.length === 0 && teamReviews.length === 0 && (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <Star size={40} className="mx-auto mb-3 text-muted opacity-50"/>
                    <p className="text-muted">No reviews to complete. Check back when a review cycle is launched.</p>
                </Card>
            )}
        </>
    );
};

const ReviewDetail = ({ review, isHR, user, onBack, onRefresh }) => {
    const [data,         setData]    = useState(null);
    const [competencies, setComp]    = useState([]);
    const [ratings,      setRatings] = useState({});
    const [form,         setForm]    = useState({});
    const [loading,      setLoading] = useState(true);
    const [saving,       setSaving]  = useState(false);
    useEffect(() => {
        Promise.all([
            axiosInstance.get(`/performance/reviews/${review.id}`),
            axiosInstance.get('/performance/competencies'),
        ]).then(([r, c]) => {
            const d = r.data.data;
            setData(d);
            setComp(c.data.data || []);
            setForm({
                overall_rating:   d.overall_rating   || '',
                strengths:        d.strengths         || '',
                improvements:     d.improvements      || '',
                goals_next_cycle: d.goals_next_cycle  || '',
                comments:         d.comments          || '',
            });
            const existing = {};
            (d.competency_ratings || []).forEach(cr => {
                if (cr.rater_id === user?.id)
                    existing[cr.competency_id] = { rating: cr.rating, comment: cr.comment||'' };
            });
            setRatings(existing);
        }).catch(() => toast.error('Failed to load'))
          .finally(() => setLoading(false));
    }, [review.id]);
    const canEdit = data?.reviewer_id === user?.id && data?.status !== 'completed';
    const save = async (submit=false) => {
        setSaving(true);
        try {
            if (Object.keys(ratings).length > 0) {
                await axiosInstance.post(`/performance/reviews/${review.id}/ratings`, {
                    ratings: Object.entries(ratings).map(([cid, r]) => ({
                        competency_id: parseInt(cid), rating: r.rating, comment: r.comment,
                    })),
                });
            }
            await axiosInstance.put(`/performance/reviews/${review.id}`, {
                ...form, status: submit ? 'completed' : 'in_progress',
            });
            toast.success(submit ? 'Review submitted!' : 'Draft saved');
            if (submit) { onBack(); onRefresh(); }
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };
    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    if (!data)   return null;
    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>← Back</Button>
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{background:'linear-gradient(135deg,#eef2ff,#e0e7ff)'}}>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="fw-bold mb-1">
                            {data.review_type==='self' ? 'Self Assessment' : `Review: ${data.reviewee_name}`}
                        </h5>
                        <div className="text-muted small">
                            Cycle: {data.cycle} · Type: {data.review_type} · Due: {formatDate(data.due_date)}
                        </div>
                        <div className="mt-2">
                            <Badge bg={REVIEW_STATUS[data.status]?.bg||'secondary'}>
                                {REVIEW_STATUS[data.status]?.label}
                            </Badge>
                        </div>
                    </Col>
                    {data.weighted_rating && (
                        <Col xs="auto" className="text-center">
                            <div style={{position:'relative',display:'inline-block'}}>
                                <ProgressRing pct={(data.weighted_rating/5)*100} size={72} stroke={6}/>
                                <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                    <span className="fw-bold">{data.weighted_rating}</span>
                                </div>
                            </div>
                            <div className="text-muted small">Weighted</div>
                        </Col>
                    )}
                </Row>
            </Card>
            <Row className="g-4">
                <Col lg={7}>
                    {competencies.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <h6 className="fw-bold mb-3">Competency Ratings</h6>
                            {competencies.map(c => (
                                <div key={c.id} className="mb-4">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="fw-medium small">{c.name}</span>
                                        <Badge bg="light" text="dark" style={{fontSize:9}}>{c.weightage}% weight</Badge>
                                    </div>
                                    {c.description && <div className="text-muted" style={{fontSize:11}}>{c.description}</div>}
                                    <div className="mt-2">
                                        <StarInput value={ratings[c.id]?.rating||0} readOnly={!canEdit}
                                            onChange={v => setRatings(p => ({...p,[c.id]:{...p[c.id],rating:v}}))}/>
                                    </div>
                                    {canEdit && (
                                        <Form.Control className="mt-1" size="sm" placeholder="Comment (optional)"
                                            value={ratings[c.id]?.comment||''}
                                            onChange={e => setRatings(p => ({...p,[c.id]:{...p[c.id],comment:e.target.value}}))}/>
                                    )}
                                </div>
                            ))}
                        </Card>
                    )}
                    <Card className="border-0 shadow-sm p-4">
                        <h6 className="fw-bold mb-3">Written Feedback</h6>
                        <Row className="g-3">
                            {[
                                {field:'strengths',label:'Strengths',ph:'What went well?'},
                                {field:'improvements',label:'Areas for Improvement',ph:'What could be better?'},
                                {field:'goals_next_cycle',label:'Goals for Next Cycle',ph:'Key objectives'},
                                {field:'comments',label:'Additional Comments',ph:'Any other feedback…'},
                            ].map(f => (
                                <Col key={f.field} xs={12}>
                                    <Form.Label className="small fw-bold">{f.label}</Form.Label>
                                    <Form.Control as="textarea" rows={3}
                                        placeholder={f.ph} value={form[f.field]||''}
                                        readOnly={!canEdit}
                                        onChange={e => setForm(p => ({...p,[f.field]:e.target.value}))}/>
                                </Col>
                            ))}
                        </Row>
                        {canEdit && (
                            <div className="d-flex gap-2 mt-4">
                                <Button variant="light" className="flex-grow-1"
                                    onClick={() => save(false)} disabled={saving}>
                                    {saving ? <Spinner size="sm"/> : 'Save Draft'}
                                </Button>
                                <Button variant="success" className="flex-grow-1 fw-bold"
                                    onClick={() => save(true)} disabled={saving}>
                                    {saving ? <Spinner size="sm"/> : '✓ Submit Review'}
                                </Button>
                            </div>
                        )}
                    </Card>
                </Col>
                <Col lg={5}>
                    {data.goals?.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4 p-4">
                            <h6 className="fw-bold mb-3">Goals this cycle</h6>
                            {data.goals.map(g => (
                                <div key={g.id} className="mb-3">
                                    <div className="small fw-medium mb-1">{g.title}</div>
                                    <div className="d-flex align-items-center gap-2">
                                        <ProgressBar now={parseFloat(g.progress_pct||0)}
                                            variant={g.status==='completed'?'success':'primary'}
                                            style={{flex:1,height:6}}/>
                                        <span style={{fontSize:11}}>{g.progress_pct||0}%</span>
                                    </div>
                                </div>
                            ))}
                        </Card>
                    )}
                    {data.peer_feedback?.length > 0 && (
                        <Card className="border-0 shadow-sm p-4">
                            <h6 className="fw-bold mb-3">Peer Feedback</h6>
                            {data.peer_feedback.map(p => (
                                <div key={p.id} className="mb-3 p-3 rounded" style={{background:'#f8fafc',fontSize:13}}>
                                    <div className="fw-medium small">{p.reviewer_name}</div>
                                    <div className="text-muted mt-1">{p.feedback}</div>
                                </div>
                            ))}
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// FEEDBACK TAB
// ════════════════════════════════════════════════════════════════════════════
const FeedbackTab = ({ isHR }) => {
    const [received, setReceived] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [showGive, setShowGive] = useState(false);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/feedback/my')
            .then(r => setReceived(r.data.data||[]))
            .catch(()=>{})
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);
    const COLORS = { recognition:'#059669', constructive:'#4f46e5', general:'#0891b2', peer_review:'#d97706' };
    const ICONS  = { recognition:'🌟', constructive:'💡', general:'💬', peer_review:'👥' };
    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button size="sm" variant="primary" onClick={() => setShowGive(true)}>
                    <ThumbsUp size={14} className="me-1"/>Give Feedback
                </Button>
            </div>
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : received.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <MessageSquare size={40} className="mx-auto mb-3 text-muted opacity-50"/>
                    <p className="text-muted">No feedback received yet.</p>
                </Card>
            ) : (
                <Row className="g-3">
                    {received.map(f => (
                        <Col key={f.id} xs={12} md={6}>
                            <Card className="border-0 shadow-sm h-100 p-4"
                                style={{borderLeft:`4px solid ${COLORS[f.feedback_type]||'#4f46e5'}`}}>
                                <div className="d-flex justify-content-between mb-2">
                                    <div style={{fontSize:20}}>{ICONS[f.feedback_type]||'💬'}</div>
                                    <Badge bg="light" text="dark" style={{fontSize:10}}>
                                        {f.feedback_type?.replace('_',' ')}
                                    </Badge>
                                </div>
                                <p className="mb-3" style={{fontSize:14}}>{f.feedback}</p>
                                <div className="text-muted small mt-auto">
                                    From: <strong>{f.giver_name}</strong> · {new Date(f.created_at).toLocaleDateString('en-IN')}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {showGive && (
                <GiveFeedbackModal
                    onClose={() => setShowGive(false)}
                    onDone={() => { setShowGive(false); load(); }}/>
            )}
        </>
    );
};

// ── Give Feedback Modal — VALIDATIONS ────────────────────────────────────
const validateFeedback = (form) => {
    const e = {};
    if (!form.receiver_id) e.receiver_id = 'Please select a person to give feedback to';
    const fb = form.feedback?.trim();
    if (!fb)               e.feedback = 'Feedback message is required';
    else if (fb.length<10) e.feedback = 'Please write at least 10 characters';
    else if (fb.length>1000) e.feedback = 'Feedback cannot exceed 1000 characters';
    return e;
};

const GiveFeedbackModal = ({ onClose, onDone }) => {
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        receiver_id:'', feedback:'', feedback_type:'recognition', is_public:true,
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    useEffect(() => {
        axiosInstance.get('/employees')
            .then(r => setEmployees(r.data.data?.employees || r.data.data || []))
            .catch(()=>{});
    }, []);

    const setField = (k,v) => {
        setForm(p=>({...p,[k]:v}));
        if (attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validateFeedback(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/performance/feedback', form);
            toast.success('Feedback sent! 🎉');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const fbLen = form.feedback?.length || 0;

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Give Feedback</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">To *</Form.Label>
                        <Form.Select value={form.receiver_id}
                            isInvalid={attempted && !!errors.receiver_id}
                            onChange={e => setField('receiver_id', e.target.value)}>
                            <option value="">— Select person —</option>
                            {employees.map(e => (
                                <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.receiver_id}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Type</Form.Label>
                        <div className="d-flex gap-2 flex-wrap">
                            {FEEDBACK_TYPES.map(t => (
                                <div key={t.key}
                                    className="p-2 px-3 rounded border"
                                    style={{
                                        cursor:'pointer', fontSize:13,
                                        background: form.feedback_type===t.key ? '#eef2ff' : undefined,
                                        borderColor: form.feedback_type===t.key ? '#4f46e5' : '#e5e7eb',
                                    }}
                                    onClick={() => setField('feedback_type', t.key)}>
                                    {t.label}
                                    <div className="text-muted" style={{fontSize:10}}>{t.desc}</div>
                                </div>
                            ))}
                        </div>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Message * <span className="text-muted fw-normal">(min 10 characters)</span></span>
                            <span className={fbLen>900?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {fbLen}/1000
                            </span>
                        </Form.Label>
                        <Form.Control as="textarea" rows={4} maxLength={1000}
                            placeholder="Write your feedback…"
                            value={form.feedback}
                            isInvalid={attempted && !!errors.feedback}
                            onChange={e => setField('feedback', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.feedback}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Check type="switch" label="Visible to the employee"
                            checked={form.is_public}
                            onChange={e => setField('is_public', e.target.checked)}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : '🚀 Send Feedback'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// APPRAISALS TAB — unchanged (no forms)
// ════════════════════════════════════════════════════════════════════════════
const AppraisalsTab = ({ isHR }) => {
    const [appraisals,   setAppraisals] = useState([]);
    const [myAppraisals, setMy]         = useState([]);
    const [loading,      setLoading]    = useState(true);
    const load = () => {
        setLoading(true);
        const calls = [axiosInstance.get('/performance/appraisals/my')];
        if (isHR) calls.push(axiosInstance.get('/performance/appraisals'));
        Promise.all(calls)
            .then(([my, all]) => { setMy(my.data.data||[]); if (all) setAppraisals(all.data.data||[]); })
            .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(() => { load(); }, []);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    const approve = async (id, action) => {
        try {
            await axiosInstance.patch(`/performance/appraisals/${id}/approve`, { action });
            toast.success(`Appraisal ${action}d`); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };
    const fmtCurrency = n => n ? `₹${parseFloat(n).toLocaleString('en-IN',{maximumFractionDigits:0})}` : '₹0';
    return (
        <>
            {myAppraisals.length > 0 && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">My Appraisals</Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th className="ps-4">Cycle</th><th>Rating</th>
                                <th>Increment</th><th>Amount</th><th className="pe-4">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {myAppraisals.map(a => (
                                <tr key={a.id}>
                                    <td className="ps-4 fw-medium">{a.cycle||formatDate(a.appraisal_date)}</td>
                                    <td>{a.rating?<StarInput value={Math.round(a.rating)} readOnly/>:'—'}</td>
                                    <td>{a.increment_percentage?`${a.increment_percentage}%`:'—'}</td>
                                    <td className="fw-bold text-success">{fmtCurrency(a.increment_amount)}</td>
                                    <td className="pe-4">
                                        <Badge bg={a.status==='approved'?'success':a.status==='rejected'?'danger':'warning'}>
                                            {a.status}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {isHR && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">All Appraisals</Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th className="ps-4">Employee</th><th>Rating</th>
                                <th>Increment</th><th>Status</th>
                                <th className="pe-4 text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {appraisals.map(a => (
                                <tr key={a.id}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{a.employee_name}</div>
                                        <div className="text-muted" style={{fontSize:10}}>{a.department}</div>
                                    </td>
                                    <td>{a.rating?<StarInput value={Math.round(a.rating)} readOnly/>:'—'}</td>
                                    <td>
                                        <div>{a.increment_percentage}%</div>
                                        <div className="text-success fw-bold" style={{fontSize:11}}>
                                            +{fmtCurrency(a.increment_amount)}
                                        </div>
                                    </td>
                                    <td>
                                        <Badge bg={a.status==='approved'?'success':a.status==='rejected'?'danger':'warning'}>
                                            {a.status}
                                        </Badge>
                                    </td>
                                    <td className="pe-4 text-end">
                                        {a.status==='pending' && (
                                            <div className="d-flex gap-1 justify-content-end">
                                                <Button size="sm" variant="success" className="py-0"
                                                    onClick={()=>approve(a.id,'approve')}>✓</Button>
                                                <Button size="sm" variant="outline-danger" className="py-0"
                                                    onClick={()=>approve(a.id,'reject')}>✗</Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// CYCLES TAB (HR only)
// ════════════════════════════════════════════════════════════════════════════
const CyclesTab = () => {
    const [cycles,  setCycles]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/cycles')
            .then(r => setCycles(r.data.data||[]))
            .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(() => { load(); }, []);
    const launch = async (id) => {
        // window.confirm kept intentionally — launching a cycle creates records for ALL employees
        // and cannot be undone, so a native browser confirm is appropriate here.
        if (!window.confirm('Launch this cycle? Review records will be created for all active employees.')) return;
        try {
            const res = await axiosInstance.post(`/performance/cycles/${id}/launch`);
            toast.success(res.data.message); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };
    const STATUS_COLOR = { draft:'secondary', active:'success', closed:'primary' };
    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
                    <Plus size={14} className="me-1"/>New Cycle
                </Button>
            </div>
            {cycles.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <RefreshCw size={36} className="mx-auto mb-2 opacity-50"/>
                    <p>No review cycles yet. Create one to start the performance review process.</p>
                </Card>
            ) : (
                <Row className="g-3">
                    {cycles.map(c => (
                        <Col key={c.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm p-4 h-100">
                                <div className="d-flex justify-content-between mb-2">
                                    <Badge bg={STATUS_COLOR[c.status]||'secondary'}>{c.status}</Badge>
                                    <Badge bg="light" text="dark">{c.cycle_type}</Badge>
                                </div>
                                <h6 className="fw-bold">{c.name}</h6>
                                <div className="text-muted small mb-3">
                                    {formatDate(c.start_date)} → {formatDate(c.end_date)}
                                </div>
                                {c.total_reviews > 0 && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span>Reviews</span>
                                            <span>{c.completed_reviews}/{c.total_reviews}</span>
                                        </div>
                                        <ProgressBar
                                            now={c.total_reviews?(c.completed_reviews/c.total_reviews)*100:0}
                                            style={{height:6}} variant="success"/>
                                    </div>
                                )}
                                {c.status==='draft' && (
                                    <Button size="sm" variant="success" className="mt-auto" onClick={()=>launch(c.id)}>
                                        <Zap size={13} className="me-1"/>Launch Cycle
                                    </Button>
                                )}
                                {c.status==='active' && (
                                    <Button size="sm" variant="outline-secondary" className="mt-auto"
                                        onClick={async()=>{
                                            await axiosInstance.post(`/performance/cycles/${c.id}/close`);
                                            toast.success('Cycle closed'); load();
                                        }}>
                                        Close Cycle
                                    </Button>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {showNew && (
                <CycleFormModal
                    onClose={() => setShowNew(false)}
                    onDone={() => { setShowNew(false); load(); }}/>
            )}
        </>
    );
};

// ── Cycle Form Modal — VALIDATIONS ────────────────────────────────────────
const validateCycle = (form) => {
    const e = {};
    const name = form.name?.trim();
    if (!name)              e.name = 'Cycle name is required';
    else if (name.length<2) e.name = 'Name must be at least 2 characters';
    else if (name.length>150) e.name = 'Name cannot exceed 150 characters';

    if (!form.start_date)   e.start_date = 'Start date is required';
    if (!form.end_date)     e.end_date   = 'End date is required';

    if (form.start_date && form.end_date) {
        if (form.end_date <= form.start_date)
            e.end_date = 'End date must be after start date';
        else {
            const inRange = (d) => !d || (d >= form.start_date && d <= form.end_date);
            if (!inRange(form.self_review_deadline))
                e.self_review_deadline = 'Must be within the cycle dates';
            if (!inRange(form.peer_review_deadline))
                e.peer_review_deadline = 'Must be within the cycle dates';
            if (!inRange(form.manager_review_deadline))
                e.manager_review_deadline = 'Must be within the cycle dates';
        }
    }
    return e;
};

const CycleFormModal = ({ onClose, onDone }) => {
    const [form, setForm] = useState({
        name:'', cycle_type:'annual',
        start_date:'', end_date:'',
        self_review_deadline:'', peer_review_deadline:'', manager_review_deadline:'',
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    const setField = (k,v) => {
        setForm(p=>({...p,[k]:v}));
        if (attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validateCycle(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/performance/cycles', form);
            toast.success('Cycle created');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">New Review Cycle</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={8}>
                        <Form.Label className="small fw-bold">Cycle Name *</Form.Label>
                        <Form.Control placeholder="e.g. Annual Review 2026"
                            value={form.name} maxLength={150}
                            isInvalid={attempted && !!errors.name}
                            onChange={e => setField('name', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Type</Form.Label>
                        <Form.Select value={form.cycle_type}
                            onChange={e => setField('cycle_type', e.target.value)}>
                            {CYCLE_TYPES.map(t => <option key={t}>{t}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Start Date *</Form.Label>
                        <Form.Control type="date" value={form.start_date}
                            isInvalid={attempted && !!errors.start_date}
                            onChange={e => setField('start_date', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.start_date}</Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">End Date *</Form.Label>
                        <Form.Control type="date" value={form.end_date}
                            min={form.start_date||undefined}
                            isInvalid={attempted && !!errors.end_date}
                            onChange={e => setField('end_date', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.end_date}</Form.Control.Feedback>
                    </Col>
                    {[
                        {field:'self_review_deadline',    label:'Self Review Deadline'    },
                        {field:'peer_review_deadline',    label:'Peer Review Deadline'    },
                        {field:'manager_review_deadline', label:'Manager Review Deadline' },
                    ].map(f => (
                        <Col key={f.field} xs={12} md={4}>
                            <Form.Label className="small fw-bold">{f.label}</Form.Label>
                            <Form.Control type="date" value={form[f.field]}
                                min={form.start_date||undefined}
                                max={form.end_date||undefined}
                                isInvalid={attempted && !!errors[f.field]}
                                onChange={e => setField(f.field, e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors[f.field]}</Form.Control.Feedback>
                        </Col>
                    ))}
                    {form.start_date && form.end_date && (
                        <Col xs={12}>
                            <div className="p-2 rounded bg-light small text-muted">
                                Deadlines must fall between{' '}
                                <strong>{formatDate(form.start_date)}</strong> and{' '}
                                <strong>{formatDate(form.end_date)}</strong>
                            </div>
                        </Col>
                    )}
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Create Cycle'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// PIP TAB (HR only)
// ════════════════════════════════════════════════════════════════════════════
const PIPTab = () => {
    const [pips,    setPips]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/pip')
            .then(r => setPips(r.data.data||[]))
            .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(() => { load(); }, []);
    const closeWithOutcome = async (id, outcome) => {
        try {
            await axiosInstance.patch(`/performance/pip/${id}/outcome`, { outcome });
            toast.success(`PIP closed: ${outcome}`); load();
        } catch { toast.error('Failed'); }
    };
    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button size="sm" variant="danger" onClick={() => setShowNew(true)}>
                    <AlertTriangle size={14} className="me-1"/>Create PIP
                </Button>
            </div>
            {pips.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <AlertTriangle size={36} className="mx-auto mb-2 opacity-50"/>
                    <p>No PIPs created. Use PIPs to formally manage performance improvement.</p>
                </Card>
            ) : (
                <Row className="g-3">
                    {pips.map(p => (
                        <Col key={p.id} xs={12} md={6}>
                            <Card className="border-0 shadow-sm p-4 h-100"
                                style={{borderLeft:`4px solid ${p.status==='active'?'#dc2626':'#6b7280'}`}}>
                                <div className="d-flex justify-content-between mb-2">
                                    <Badge bg={p.status==='active'?'danger':'secondary'}>{p.status}</Badge>
                                    {p.outcome && <Badge bg="light" text="dark">{p.outcome}</Badge>}
                                </div>
                                <h6 className="fw-bold">{p.employee_name}</h6>
                                <div className="text-muted small mb-2">{p.department}</div>
                                <div className="fw-medium small mb-2">{p.title}</div>
                                <div className="text-muted small mb-3">
                                    {formatDate(p.start_date)} → {formatDate(p.end_date)} · {p.review_frequency}
                                </div>
                                {p.status==='active' && (
                                    <div className="d-flex gap-1 flex-wrap">
                                        {['completed','extended','terminated'].map(o => (
                                            <Button key={o} size="sm"
                                                variant={o==='completed'?'success':o==='terminated'?'danger':'outline-secondary'}
                                                className="py-0"
                                                onClick={() => closeWithOutcome(p.id, o)}>
                                                {o}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {showNew && (
                <PIPFormModal
                    onClose={() => setShowNew(false)}
                    onDone={() => { setShowNew(false); load(); }}/>
            )}
        </>
    );
};

// ── PIP Form Modal — VALIDATIONS ─────────────────────────────────────────
const validatePIP = (form) => {
    const e = {};
    if (!form.employee_id) e.employee_id = 'Please select an employee';

    const title = form.title?.trim();
    if (!title)              e.title = 'PIP title is required';
    else if (title.length<5) e.title = 'Title must be at least 5 characters';
    else if (title.length>200) e.title = 'Title cannot exceed 200 characters';

    const checkLongField = (key, label) => {
        const v = form[key]?.trim();
        if (!v)              e[key] = `${label} is required`;
        else if (v.length<20) e[key] = `${label} needs more detail (min 20 characters)`;
        else if (v.length>2000) e[key] = `${label} cannot exceed 2000 characters`;
    };
    checkLongField('reason',            'Reason for PIP');
    checkLongField('improvement_areas', 'Areas for Improvement');
    checkLongField('success_criteria',  'Success Criteria');

    if (!form.start_date) e.start_date = 'Start date is required';
    if (!form.end_date)   e.end_date   = 'End date is required';

    if (form.start_date && form.end_date) {
        if (form.end_date <= form.start_date) {
            e.end_date = 'End date must be after start date';
        } else {
            // PIP must be at least 2 weeks
            const diffDays = (new Date(form.end_date) - new Date(form.start_date)) / 86400000;
            if (diffDays < 14)
                e.end_date = 'PIP duration must be at least 2 weeks';
        }
    }
    return e;
};

const PIPFormModal = ({ onClose, onDone }) => {
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        employee_id:'', title:'', reason:'',
        improvement_areas:'', success_criteria:'',
        start_date:'', end_date:'', review_frequency:'weekly',
    });
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);

    useEffect(() => {
        axiosInstance.get('/employees')
            .then(r => setEmployees(r.data.data?.employees || r.data.data || []))
            .catch(()=>{});
    }, []);

    const setField = (k,v) => {
        setForm(p=>({...p,[k]:v}));
        if (attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const submit = async () => {
        setAttempted(true);
        const errs = validatePIP(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/performance/pip', form);
            toast.success('PIP created. Employee has been notified.');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
        finally { setSaving(false); }
    };

    // Character counter helper
    const cc = (field, max) => {
        const len = form[field]?.length || 0;
        return (
            <span className={len > max*0.9 ? 'text-danger' : 'text-muted'} style={{fontSize:11}}>
                {len}/{max}
            </span>
        );
    };

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <AlertTriangle size={18} className="me-2 text-danger"/>
                    Create Performance Improvement Plan
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="small py-2 mb-3">
                    A PIP is a serious action. Ensure you have documented performance issues
                    and discussed them with HR before proceeding.
                </Alert>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Employee *</Form.Label>
                        <Form.Select value={form.employee_id}
                            isInvalid={attempted && !!errors.employee_id}
                            onChange={e => setField('employee_id', e.target.value)}>
                            <option value="">— Select employee —</option>
                            {employees.map(e => (
                                <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.employee_id}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>PIP Title *</span>{cc('title',200)}
                        </Form.Label>
                        <Form.Control placeholder="e.g. Performance Improvement Plan — Q2 2026"
                            value={form.title} maxLength={200}
                            isInvalid={attempted && !!errors.title}
                            onChange={e => setField('title', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                    </Col>
                    {[
                        {key:'reason',           label:'Reason for PIP',        ph:'Describe the specific performance issues observed…'},
                        {key:'improvement_areas',label:'Areas for Improvement', ph:'List specific behaviours or metrics that need improvement…'},
                        {key:'success_criteria', label:'Success Criteria',      ph:'Define measurable goals that indicate successful completion…'},
                    ].map(f => (
                        <Col key={f.key} xs={12}>
                            <Form.Label className="small fw-bold d-flex justify-content-between">
                                <span>{f.label} * <span className="text-muted fw-normal">(min 20 chars)</span></span>
                                {cc(f.key, 2000)}
                            </Form.Label>
                            <Form.Control as="textarea" rows={f.key==='reason'?2:3}
                                maxLength={2000} placeholder={f.ph}
                                value={form[f.key]}
                                isInvalid={attempted && !!errors[f.key]}
                                onChange={e => setField(f.key, e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors[f.key]}</Form.Control.Feedback>
                        </Col>
                    ))}
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Start Date *</Form.Label>
                        <Form.Control type="date" value={form.start_date}
                            min={todayStr()}
                            isInvalid={attempted && !!errors.start_date}
                            onChange={e => setField('start_date', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.start_date}</Form.Control.Feedback>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">End Date *</Form.Label>
                        <Form.Control type="date" value={form.end_date}
                            min={form.start_date||todayStr()}
                            isInvalid={attempted && !!errors.end_date}
                            onChange={e => setField('end_date', e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.end_date}</Form.Control.Feedback>
                        <Form.Text className="text-muted">Min 2 weeks from start</Form.Text>
                    </Col>
                    <Col xs={4}>
                        <Form.Label className="small fw-bold">Review Frequency</Form.Label>
                        <Form.Select value={form.review_frequency}
                            onChange={e => setField('review_frequency', e.target.value)}>
                            {['daily','weekly','bi-weekly','monthly'].map(f => <option key={f}>{f}</option>)}
                        </Form.Select>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="danger" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Create PIP & Notify Employee'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PerformancePage;
